import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useProfile } from '@/features/auth/useProfile';
import { formatDate, formatMoney } from '@/lib/format';
import { CancelDealDialog } from './CancelDealDialog';
import { CreateReservationDialog } from './CreateReservationDialog';
import { useConvertToContract, useLeadDeals, type Deal } from './useDeals';

const STATUS_STYLES: Record<string, string> = {
  reserved: 'bg-amber-100 text-amber-900',
  contracted: 'bg-green-100 text-green-900',
  cancelled: 'bg-slate-200 text-slate-700',
  refunded: 'bg-red-100 text-red-900',
};

/**
 * Deals on the lead page (#23).
 *
 * A lead has at most one live deal in practice, but cancelled ones are kept and
 * shown: a unit that fell through twice is a fact worth seeing on the record,
 * and it is the only place the cancellation reason surfaces.
 */
export function DealsPanel({ leadId, leadRevision }: { leadId: string; leadRevision?: string }) {
  const { t } = useTranslation();
  const { data: me } = useProfile();
  const { data: deals, isPending, isError } = useLeadDeals(leadId);
  const convert = useConvertToContract();

  const [reserving, setReserving] = useState(false);
  const [cancelling, setCancelling] = useState<Deal | undefined>();
  const [error, setError] = useState<string | null>(null);

  const role = me?.role;
  const canReserve = !!role && ['sales', 'team_leader', 'sales_manager', 'admin'].includes(role);
  // Supervisory: a rep who could cancel their own deal could also quietly undo a
  // figure a manager has already reported. Mirrors cancel_deal()'s own check.
  const canCancel = !!role && ['sales_manager', 'admin', 'finance'].includes(role);

  const rows = deals ?? [];
  const live = rows.filter((d) => d.status === 'reserved' || d.status === 'contracted');

  const doConvert = (deal: Deal) => {
    setError(null);
    convert.mutateAsync({ dealId: deal.id }).catch((cause: unknown) => {
      setError(cause instanceof Error ? cause.message : t('common.error'));
    });
  };

  if (isPending) return <Spinner />;
  if (isError) return <Alert tone="error">{t('common.error')}</Alert>;

  return (
    <div className="flex flex-col gap-3">
      {error ? <Alert tone="error">{error}</Alert> : null}

      {rows.length === 0 ? (
        <p className="text-sm text-slate-500">{t('deals.none')}</p>
      ) : (
        rows.map((deal) => (
          <article key={deal.id} className="rounded-xl border border-slate-200 p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold text-slate-900">
                  {deal.units?.projects?.name ?? '—'} · {deal.units?.unit_code ?? '—'}
                </h3>
                <p className="text-xs text-slate-500">
                  {t(`dealTypes.${deal.type}` as 'dealTypes.reservation')} ·{' '}
                  {formatDate(deal.reservation_date)}
                  {deal.contract_date
                    ? ` · ${t('deals.contractDate')} ${formatDate(deal.contract_date)}`
                    : ''}
                </p>
              </div>

              <div className="text-end">
                <div className="text-sm font-semibold text-slate-900" dir="ltr">
                  {formatMoney(deal.unit_value)}
                </div>
                <span
                  className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                    STATUS_STYLES[deal.status] ?? 'bg-slate-100 text-slate-700'
                  }`}
                >
                  {t(`dealStatuses.${deal.status}` as 'dealStatuses.reserved')}
                </span>
              </div>
            </div>

            {deal.cancellation_reason ? (
              <p className="mt-2 text-xs text-slate-600">
                {t('deals.cancelReason')}: {deal.cancellation_reason}
              </p>
            ) : null}

            {deal.status === 'reserved' || deal.status === 'contracted' ? (
              <div className="mt-3 flex flex-wrap gap-2">
                {deal.status === 'reserved' && canReserve ? (
                  <Button
                    variant="secondary"
                    loading={convert.isPending}
                    onClick={() => doConvert(deal)}
                  >
                    {t('deals.convertToContract')}
                  </Button>
                ) : null}
                {canCancel ? (
                  <Button variant="ghost" onClick={() => setCancelling(deal)}>
                    {t('deals.cancelDeal')}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </article>
        ))
      )}

      {/* One live deal per unit is enforced in the database; one live deal per
          lead is a workflow choice, so the button simply steps aside while one
          is open rather than the click failing. */}
      {canReserve && live.length === 0 ? (
        <div>
          <Button onClick={() => setReserving(true)}>{t('deals.createReservation')}</Button>
        </div>
      ) : null}

      <CreateReservationDialog
        open={reserving}
        onClose={() => setReserving(false)}
        leadId={leadId}
        leadRevision={leadRevision}
      />
      <CancelDealDialog
        open={Boolean(cancelling)}
        deal={cancelling}
        onClose={() => setCancelling(undefined)}
      />
    </div>
  );
}
